// This is a temporary implementation of Revieve Makeup Advisor for evaluating their out-of-the-box solution

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type { RevieveMakeupAdvisorViewModel } from 'autogen/swagger/all';
import { MakeupAdvisor } from 'autogen/translation-keys/trans-website-shared';
import ButtonWithSpinner from 'components/Button/ButtonWithSpinner';

interface IRevieveMakeupAdvisorConfig {
    partner_id: string;
    locale: string;
    avoidCookies: boolean;
    env: string;
    disableLauncherButton: boolean;
    analytics: {
        disableUsageAnalytics: boolean;
        disableProductAnalytics: boolean;
        disableErrorReporting: boolean;
    };
    onClose: () => void;
}

const StyledButton = styled(ButtonWithSpinner)`
    position: sticky;
    bottom: ${Spacing8};
    margin-left: auto;
    z-index: 1;
`;

const StyledDiv = styled.div<{ $isOpen: boolean }>`
    display: none;

    ${({ $isOpen }) =>
        $isOpen &&
        css`
            position: absolute;
            z-index: 0;
            display: block;
            min-width: 0;
            width: 100%;
            height: 100%;
        `}
`;

function RevieveMakeupAdvisor({ config }: { config: RevieveMakeupAdvisorViewModel }) {
    const { t } = useTranslation();

    const { partnerId, locale, env } = config;

    const [isOpen, setIsOpen] = useState(false);

    const revieveConfig: IRevieveMakeupAdvisorConfig = {
        partner_id: partnerId,
        locale: locale,
        disableLauncherButton: true,
        avoidCookies: true,
        env: env,
        analytics: {
            disableUsageAnalytics: true,
            disableProductAnalytics: true,
            disableErrorReporting: true
        },
        onClose: () => {
            setIsOpen(false);
        }
    };

    const openPlugin = () => {
        setIsOpen(true);
        window.Revieve.API.show();
    };

    const loadRevieveScript = () => {
        return new Promise((resolve, reject) => {
            const existingScript = document.getElementById('revieve_script');

            if (!existingScript) {
                const script = document.createElement('script');
                script.id = 'revieve_script';
                script.src = 'https://d38knilzwtuys1.cloudfront.net/revieve-plugin-v4/revieve-plugin-loader.js';
                script.async = true;
                script.onload = () => resolve(script);
                script.onerror = () => reject(new Error('Failed to load Revieve script'));
                const firstScript = document.getElementsByTagName('script')[0];
                firstScript.parentNode?.insertBefore(script, firstScript);
            } else {
                resolve(existingScript);
            }
        });
    };

    const initRevieveConfig = () => {
        return new Promise<void>((resolve, reject) => {
            if (!window.Revieve._config()) {
                window.Revieve.Init(revieveConfig, () => {
                    resolve();
                });
            } else {
                reject();
            }
        });
    };

    const open = async () => {
        if (!window.Revieve) {
            try {
                await loadRevieveScript();
                await initRevieveConfig();
            } catch (error) {
                console.error('Error loading or initializing the plugin', error);
            }
        }

        openPlugin();
    };

    return (
        <>
            {!isOpen && (
                <StyledButton variant="outline" size="sm" onClick={open}>
                    {t(MakeupAdvisor)}
                </StyledButton>
            )}
        </>
    );
}

export default RevieveMakeupAdvisor;
